import { bool, fail, isRecord, list, num, optionalText, text } from '../../../shared/api/onecEnvelope'
import type {
  InventorySession,
  ScanResult,
  SessionRow,
  WarehousePrice,
} from '../model/types'

/**
 * Wire-формы B4b (`InventoryContracts.cs`) и их декодеры (склады — из
 * структуры базы, фича `structure`). Ответ проверяется на
 * границе: экран не получает `undefined` вместо цены. GUID приводятся к
 * нижнему регистру — так они сравниваются с ключами в адресе и в черновике.
 */
export const ONEC_INVENTORY_SCHEMAS = {
  create: 'onec.business.Inventory_Create.v1',
  saveSession: 'onec.business.Inventory_SaveSession.v1',
  clearSession: 'onec.business.Inventory_ClearSession.v1',
} as const

const guid = (value: unknown, field: string): string => text(value, field).toLowerCase()

function optionalGuid(value: unknown, field: string): string | null {
  const raw = optionalText(value, field)
  return raw === null ? null : raw.toLowerCase()
}

function optionalNumber(value: unknown, field: string): number | null {
  if (value === undefined || value === null) return null
  return num(value, field)
}

function record(raw: unknown, field: string): Record<string, unknown> {
  return isRecord(raw) ? raw : fail(`${field} — не объект`)
}

function decodePrice(raw: unknown, index: number): WarehousePrice {
  const item = record(raw, `prices[${index}]`)
  return {
    warehouseRefKey: guid(item.warehouseRefKey, 'warehouseRefKey'),
    warehouseName: text(item.warehouseName, 'warehouseName'),
    price: optionalNumber(item.price, 'price'),
  }
}

export function decodeScan(raw: unknown): ScanResult {
  const item = record(raw, 'data')
  const found = bool(item.found, 'found')
  const productRefKey = optionalGuid(item.productRefKey, 'productRefKey')
  // Найденный без ссылки — не товар, а ошибка ответа: строку с ним сервер не примет.
  if (found && productRefKey === null) fail('найденный товар без ссылки 1С')
  return {
    found,
    barcode: text(item.barcode, 'barcode'),
    productRefKey: found ? productRefKey : null,
    name: optionalText(item.name, 'name'),
    prices: list(item.prices, 'prices', decodePrice),
    stockQty: optionalNumber(item.stockQty, 'stockQty'),
  }
}

function decodeRow(raw: unknown, index: number): SessionRow {
  const item = record(raw, `rows[${index}]`)
  return {
    id: guid(item.id, 'id'),
    ...decodeScan({
      found: item.found,
      barcode: item.barcode,
      productRefKey: item.productRefKey,
      name: item.name,
      prices: item.prices,
      stockQty: item.stockQty,
    }),
    counted: optionalNumber(item.counted, 'counted'),
    reprint: bool(item.reprint, 'reprint'),
  }
}

export function decodeSession(raw: unknown): InventorySession {
  const item = record(raw, 'data')
  return {
    warehouseRefKey: guid(item.warehouseRefKey, 'warehouseRefKey'),
    warehouseName: text(item.warehouseName, 'warehouseName'),
    rows: list(item.rows, 'rows', decodeRow),
  }
}

/** Сессии нет — `data: null` (B4b): это пустой обход, а не ошибка. */
export const decodeOptionalSession = (raw: unknown): InventorySession | null =>
  raw === null || raw === undefined ? null : decodeSession(raw)

/** `ResourceRef` = `{ resourceId: { value } | string, remoteKey }`; чужой rid в ссылке — ошибка ответа. */
/** Строка в тело сохранения: все поля DTO, сервер разбирает тело строго. */
export function rowToWire(row: SessionRow) {
  return {
    id: row.id,
    barcode: row.barcode,
    productRefKey: row.found ? row.productRefKey : null,
    name: row.name,
    found: row.found,
    stockQty: row.stockQty,
    counted: row.counted,
    reprint: row.reprint,
    prices: row.prices.map((price) => ({
      warehouseRefKey: price.warehouseRefKey,
      warehouseName: price.warehouseName,
      price: price.price,
    })),
  }
}
